import { useCallback, useEffect, useMemo, useState } from 'react';

import { ArrowDown, ArrowUp, Crown, Loader2, Minus, Plus, Trash2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { analyticsApi } from '@/lib/api/analytics.api';
import { configurationsApi } from '@/lib/api/configurations.api';
import { cn } from '@/lib/utils';

import { KPI_DEFINITIONS, getGroupColor } from './types';
import {
  buildSelectionLabel,
  computeDiffPct,
  findBestGroupIndex,
  generateGroupId,
  getAvailableVersions,
  getRate,
  groupKpis,
  groupLabel,
} from './utils';

import type { CompareGroup } from './types';
import type { AggregatedGroupKpis } from './utils';
import type {
  CompareConfigurationsInput,
  CompareWindowMode,
  ConfigurationSetup,
  Direction,
} from '@repo/shared-types';

type ChannelFilter = 'ALL' | 'PSTN' | 'WEBRTC' | 'WHATSAPP' | 'WEB_WIDGET' | 'EMAIL';

const DIRECTION_OPTIONS: { value: Direction; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'INBOUND', label: 'Inbound' },
  { value: 'OUTBOUND', label: 'Outbound' },
];

const CHANNEL_OPTIONS: { value: ChannelFilter; label: string }[] = [
  { value: 'ALL', label: 'All Channels' },
  { value: 'PSTN', label: 'Phone Calls' },
  { value: 'WEBRTC', label: 'Web Calls' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'WEB_WIDGET', label: 'Web Widget' },
  { value: 'EMAIL', label: 'Email' },
];

const WINDOW_MODE_OPTIONS: { value: CompareWindowMode; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'first_days', label: 'First N days' },
  { value: 'last_days', label: 'Last N days' },
  { value: 'day_range', label: 'Day range' },
];

const numberInputClass =
  'focus:ring-primary bg-background text-foreground h-8 w-16 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700';

export const CompareKpisPage = () => {
  const [configurations, setConfigurations] = useState<ConfigurationSetup[]>([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);

  const [direction, setDirection] = useState<Direction>('ALL');
  const [channel, setChannel] = useState<ChannelFilter>('ALL');

  // Relative, per-group date window. Each group is anchored on its own first
  // call, so an absolute From/To (which differs per group) would be meaningless.
  const [windowMode, setWindowMode] = useState<CompareWindowMode>('all');
  const [firstDays, setFirstDays] = useState('7');
  const [lastDays, setLastDays] = useState('7');
  const [dayFrom, setDayFrom] = useState('1');
  const [dayTo, setDayTo] = useState('7');

  const [groups, setGroups] = useState<CompareGroup[]>(() => [
    { id: generateGroupId(), label: groupLabel(0), selections: [] },
    { id: generateGroupId(), label: groupLabel(1), selections: [] },
  ]);
  const [aggregated, setAggregated] = useState<AggregatedGroupKpis[] | null>(null);

  const [addingToGroupId, setAddingToGroupId] = useState<string | null>(null);
  const [selectedConfigId, setSelectedConfigId] = useState<string>('');
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');

  const windowValid =
    windowMode === 'all' ||
    (windowMode === 'first_days' && Number(firstDays) >= 1) ||
    (windowMode === 'last_days' && Number(lastDays) >= 1) ||
    (windowMode === 'day_range' && Number(dayFrom) >= 1 && Number(dayTo) >= Number(dayFrom));

  const buildCompareInput = useCallback(
    (): CompareConfigurationsInput => ({
      groups: groups.map((g) => ({
        id: g.id,
        versionIds: g.selections.map((s) => s.versionId),
      })),
      direction,
      timePreset: 'all',
      ...(channel !== 'ALL' ? { callTransport: channel } : {}),
      windowMode,
      ...(windowMode === 'first_days' ? { firstDays: Number(firstDays) } : {}),
      ...(windowMode === 'last_days' ? { lastDays: Number(lastDays) } : {}),
      ...(windowMode === 'day_range' ? { dayFrom: Number(dayFrom), dayTo: Number(dayTo) } : {}),
    }),
    [groups, direction, channel, windowMode, firstDays, lastDays, dayFrom, dayTo],
  );

  useEffect(() => {
    configurationsApi.getConfigurations().then((configs) => {
      setConfigurations(configs);
      setLoading(false);
    });
  }, []);

  const availableVersions = useMemo(
    () => getAvailableVersions(configurations, selectedConfigId),
    [configurations, selectedConfigId],
  );

  const handleAddGroup = useCallback(() => {
    setGroups((prev) => [
      ...prev,
      { id: generateGroupId(), label: groupLabel(prev.length), selections: [] },
    ]);
  }, []);

  const handleRemoveGroup = useCallback((groupId: string) => {
    setGroups((prev) => {
      if (prev.length <= 2) return prev;
      const filtered = prev.filter((g) => g.id !== groupId);
      return filtered.map((g, i) => ({ ...g, label: groupLabel(i) }));
    });
  }, []);

  const handleAddSelection = useCallback(() => {
    if (!selectedConfigId || !selectedVersionId || !addingToGroupId) return;

    const config = configurations.find((c) => c.id === selectedConfigId);
    const version = availableVersions.find((v) => v.id === selectedVersionId);
    if (!config || !version) return;

    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== addingToGroupId) return g;
        if (g.selections.some((s) => s.versionId === version.id)) return g;
        return {
          ...g,
          selections: [
            ...g.selections,
            {
              configurationId: config.id,
              configurationName: config.name,
              versionId: version.id,
              versionNumber: version.versionNumber,
            },
          ],
        };
      }),
    );

    setSelectedConfigId('');
    setSelectedVersionId('');
  }, [selectedConfigId, selectedVersionId, addingToGroupId, configurations, availableVersions]);

  const handleRemoveSelection = useCallback((groupId: string, versionId: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? { ...g, selections: g.selections.filter((s) => s.versionId !== versionId) }
          : g,
      ),
    );
  }, []);

  const handleCompare = useCallback(async () => {
    const allVersionIds = groups.flatMap((g) => g.selections.map((s) => s.versionId));
    if (allVersionIds.length === 0) return;

    setComparing(true);
    try {
      const data = await analyticsApi.compareConfigurations(buildCompareInput());
      setAggregated(groups.map((g) => groupKpis(data[g.id])));
    } finally {
      setComparing(false);
    }
  }, [groups, buildCompareInput]);

  const canCompare = groups.filter((g) => g.selections.length > 0).length >= 2 && windowValid;

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Compare KPIs</h1>
        <p className="text-muted-foreground mt-1">
          Compare key performance indicators across configuration versions
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-6 rounded-lg border p-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium">Direction</label>
          <div className="flex items-center gap-1 rounded-lg border p-0.5">
            {DIRECTION_OPTIONS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => setDirection(d.value)}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-all',
                  direction === d.value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium">Channel</label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value as ChannelFilter)}
            className="focus:ring-primary bg-background text-foreground h-8 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
          >
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium">Window</label>
          <div className="flex items-center gap-1 rounded-lg border p-0.5">
            {WINDOW_MODE_OPTIONS.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setWindowMode(m.value)}
                className={cn(
                  'rounded-md px-3 py-1 text-xs font-medium transition-all',
                  windowMode === m.value
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {windowMode === 'first_days' && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium">First</label>
            <input
              type="number"
              min={1}
              value={firstDays}
              onChange={(e) => setFirstDays(e.target.value)}
              className={numberInputClass}
            />
            <span className="text-muted-foreground text-xs">days of each group</span>
          </div>
        )}

        {windowMode === 'last_days' && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium">Last</label>
            <input
              type="number"
              min={1}
              value={lastDays}
              onChange={(e) => setLastDays(e.target.value)}
              className={numberInputClass}
            />
            <span className="text-muted-foreground text-xs">days of each group</span>
          </div>
        )}

        {windowMode === 'day_range' && (
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium">Day</label>
            <input
              type="number"
              min={1}
              value={dayFrom}
              onChange={(e) => setDayFrom(e.target.value)}
              className={numberInputClass}
            />
            <span className="text-muted-foreground text-xs">to</span>
            <input
              type="number"
              min={1}
              value={dayTo}
              onChange={(e) => setDayTo(e.target.value)}
              className={numberInputClass}
            />
          </div>
        )}
      </div>

      {windowMode !== 'all' && (
        <p className="text-muted-foreground -mt-3 text-xs">
          Day 1 is each group&apos;s first call, so groups that ran at different times are compared
          over the equivalent slice of their own run.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {groups.map((group, index) => (
          <GroupCard
            key={group.id}
            group={group}
            groupIndex={index}
            canRemove={groups.length > 2}
            onAdd={() => setAddingToGroupId(group.id)}
            onRemoveGroup={() => handleRemoveGroup(group.id)}
            onRemoveSelection={(versionId) => handleRemoveSelection(group.id, versionId)}
          />
        ))}

        <button
          onClick={handleAddGroup}
          className="border-muted-foreground/25 text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground flex min-h-[140px] items-center justify-center rounded-lg border-2 border-dashed transition-colors"
        >
          <div className="flex flex-col items-center gap-2">
            <Plus className="h-6 w-6" />
            <span className="text-sm font-medium">Add Group</span>
          </div>
        </button>
      </div>

      {addingToGroupId && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Add to {groups.find((g) => g.id === addingToGroupId)?.label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4">
              <div className="flex-1 space-y-1">
                <label className="text-sm font-medium">Configuration</label>
                <Select
                  value={selectedConfigId}
                  onValueChange={(v) => {
                    setSelectedConfigId(v);
                    const versions = getAvailableVersions(configurations, v);
                    const v1 = versions.find((ver) => ver.versionNumber === 1);
                    setSelectedVersionId(v1?.id ?? versions[0]?.id ?? '');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a configuration" />
                  </SelectTrigger>
                  <SelectContent>
                    {configurations.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-40 space-y-1">
                <label className="text-sm font-medium">Version</label>
                <Select
                  value={selectedVersionId}
                  onValueChange={setSelectedVersionId}
                  disabled={!selectedConfigId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Version" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableVersions.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        v{v.versionNumber}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleAddSelection}
                disabled={!selectedConfigId || !selectedVersionId}
              >
                Add
              </Button>
              <Button variant="outline" onClick={() => setAddingToGroupId(null)}>
                Done
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-center">
        <Button size="lg" onClick={handleCompare} disabled={!canCompare || comparing}>
          {comparing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Compare
        </Button>
      </div>

      {aggregated && <ResultsTable groups={groups} aggregated={aggregated} />}
    </div>
  );
};

function ResultsTable({
  groups,
  aggregated,
}: {
  groups: CompareGroup[];
  aggregated: AggregatedGroupKpis[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Results</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[200px]">KPI</TableHead>
                {groups.map((group, i) => {
                  const color = getGroupColor(i);
                  return (
                    <TableHead key={group.id} className="min-w-[160px] text-center">
                      <div className="flex flex-col items-center gap-1.5">
                        <Badge variant="outline" className={cn(color.bg, color.text, color.border)}>
                          {group.label}
                        </Badge>
                        <div className="flex flex-col items-center">
                          {group.selections.map((s) => (
                            <span key={s.versionId} className="text-muted-foreground text-xs">
                              {buildSelectionLabel(s)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </TableHead>
                  );
                })}
                <TableHead className="min-w-[100px] text-center">Best</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {KPI_DEFINITIONS.map((kpi) => {
                const values = aggregated.map((a) => a[kpi.key as keyof AggregatedGroupKpis]);
                const bestIdx = findBestGroupIndex(values, kpi.higherIsBetter);

                return (
                  <TableRow key={kpi.key}>
                    <TableCell>
                      <span className="font-medium">{kpi.label}</span>
                    </TableCell>
                    {values.map((val, i) => {
                      const isBest = bestIdx === i;
                      const diffPct =
                        bestIdx !== null && !isBest ? computeDiffPct(val, values[bestIdx]) : null;
                      const rate = getRate(aggregated[i], kpi.key);

                      return (
                        <TableCell key={groups[i].id} className="text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <span
                              className={cn(
                                'text-sm tabular-nums',
                                isBest && 'font-semibold text-emerald-600 dark:text-emerald-400',
                              )}
                            >
                              {val.toLocaleString()}
                              {rate !== null && (
                                <span className="text-muted-foreground ml-1 text-xs font-normal">
                                  ({rate.toFixed(1)}%)
                                </span>
                              )}
                              {isBest && (
                                <Crown className="ml-1 inline h-3.5 w-3.5 text-amber-500" />
                              )}
                            </span>
                            {diffPct !== null && (
                              <span
                                className={cn(
                                  'flex items-center gap-0.5 text-xs tabular-nums',
                                  diffPct > 0
                                    ? 'text-emerald-600 dark:text-emerald-400'
                                    : diffPct < 0
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-muted-foreground',
                                )}
                              >
                                {diffPct > 0 ? (
                                  <ArrowUp className="h-3 w-3" />
                                ) : diffPct < 0 ? (
                                  <ArrowDown className="h-3 w-3" />
                                ) : (
                                  <Minus className="h-3 w-3" />
                                )}
                                {Math.abs(diffPct).toFixed(1)}% vs best
                              </span>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-center">
                      {bestIdx !== null ? (
                        <Badge
                          variant="outline"
                          className={cn(
                            getGroupColor(bestIdx).bg,
                            getGroupColor(bestIdx).text,
                            getGroupColor(bestIdx).border,
                            'text-xs',
                          )}
                        >
                          {groups[bestIdx].label}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">Tied</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function GroupCard({
  group,
  groupIndex,
  canRemove,
  onAdd,
  onRemoveGroup,
  onRemoveSelection,
}: {
  group: CompareGroup;
  groupIndex: number;
  canRemove: boolean;
  onAdd: () => void;
  onRemoveGroup: () => void;
  onRemoveSelection: (versionId: string) => void;
}) {
  const color = getGroupColor(groupIndex);

  return (
    <Card className={cn(color.border, 'border')}>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={cn(color.bg, color.text, color.border)}>
            {group.label.replace('Group ', '')}
          </Badge>
          <CardTitle className="text-base">{group.label}</CardTitle>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={onAdd}>
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
          {canRemove && (
            <Button variant="ghost" size="sm" onClick={onRemoveGroup} className="h-8 w-8 p-0">
              <X className="text-muted-foreground h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {group.selections.length === 0 ? (
          <p className="text-muted-foreground text-sm">No configurations selected</p>
        ) : (
          <div className="space-y-2">
            {group.selections.map((s) => (
              <div
                key={s.versionId}
                className="flex items-center justify-between rounded-md border px-3 py-2"
              >
                <span className="text-sm">{buildSelectionLabel(s)}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemoveSelection(s.versionId)}
                  className="h-7 w-7 p-0"
                >
                  <Trash2 className="text-muted-foreground h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default CompareKpisPage;
